/** Global Imports */
import React, { useMemo, useCallback, memo } from 'react';
import { useSelector } from 'react-redux';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';

/** Component Imports */
import { styles } from './styles';
import TagsComponent from 'components/common/tags';
import ImageStacker from 'components/common/image-stacker';
import UserImage from 'components/common/user-image';
import { SecondaryButton } from 'components/common/buttons';
import useApplicationPermission from 'components/common/hooks/useCollabPermission';

function ProgramCard({
  programInfo,
  userId,
  navigateToProgramDetails,
  activeMode,
  handleRequestAccessButtonClick,
}) {
  const classes = styles();
  const globalUsers = useSelector((state) => state.userReducer.globalUsers);
  const { collabSuperAdminRole } = useApplicationPermission();
  const {
    programId,
    programName,
    productPlatformName,
    ppColorCode,
    businessUnitName,
    buColorCode,
    ownerId,
    users = [],
    gitProgram,
  } = programInfo;

  const owner = useMemo(() => {
    const ownerInfo = globalUsers.find((x) => x.userID == ownerId);
    if (ownerInfo !== undefined) {
      return {
        email: ownerInfo?.email || '',
        firstName: ownerInfo.firstName,
        lastName: ownerInfo.lastName,
        fullName: `${ownerInfo.firstName} ${ownerInfo.lastName}`,
      };
    } else {
      return null;
    }
  }, [globalUsers, ownerId]);

  const programUsers = useMemo(() => {
    return users
      .map((user) => {
        const userDetails = globalUsers.find(
          (x) => x.userID == parseInt(user) && ownerId !== parseInt(user),
        );
        if (userDetails) {
          return {
            id: user,
            userId: user,
            email: userDetails?.email || '',
            firstName: userDetails.firstName,
            lastName: userDetails.lastName,
            url: '',
          };
        } else {
          return {};
        }
      })
      .filter((user) => user !== undefined && Object.entries(user).length > 0);
  }, [users, globalUsers]);

  const programTags = useMemo(() => {
    const defaultTags = [
      {
        name: productPlatformName,
        bgColorCode: ppColorCode,
        fgColorCode: ppColorCode.replace(', 0.1', ''),
      },
      {
        name: businessUnitName,
        bgColorCode: buColorCode,
        fgColorCode: buColorCode.replace(', 0.1', ''),
      },
    ];
    const gitTag = {
      name: 'GitHub',
      bgColorCode: 'rgb(137 200 252)',
      fgColorCode: 'rgb(255, 255, 255)',
    };
    return gitProgram ? [...defaultTags, gitTag] : defaultTags;
  }, [productPlatformName, businessUnitName, gitProgram]);

  const handleProgramCardClick = useCallback(() => {
    navigateToProgramDetails({
      programId,
    });
  }, [programId]);

  return (
    <Card
      className={classes.programCard}
      onClick={
        programInfo.users.includes(userId) ||
        (typeof collabSuperAdminRole !== 'undefined' && collabSuperAdminRole)
          ? handleProgramCardClick
          : undefined
      }
    >
      <CardActionArea className={classes.cardActionArea}>
        <CardContent className={classes.cardContent}>
          <Typography
            className={gitProgram ? classes.gitProgramTitle : classes.title}
          >
            {programName}
          </Typography>
          <TagsComponent tags={programTags} />
          <div className={classes.programUsers}>
            <span className={classes.programOwnerImage}>
              {owner !== null && (
                <UserImage
                  email={owner?.email || ''}
                  firstName={owner.firstName || ''}
                  lastName={owner.lastName || ''}
                  bgColor="#1891F6"
                />
              )}
            </span>
            <ImageStacker imageList={programUsers} maxImagesToDisplay={4} />
            <span>
              {activeMode === 'all' &&
                (programInfo.users.includes(
                  userId,
                ) ? null : programInfo.requesters.includes(userId) ? (
                  <SecondaryButton
                    classes={{ root: classes.btnPendingAccess }}
                    variant="outlined"
                    startIcon={<LockOutlinedIcon />}
                  >
                    Pending
                  </SecondaryButton>
                ) : (
                  <SecondaryButton
                    classes={{ root: classes.btnRequestAccess }}
                    variant="outlined"
                    onClick={(e) =>
                      handleRequestAccessButtonClick(e, programInfo, owner)
                    }
                  >
                    Request access
                  </SecondaryButton>
                ))}
            </span>
          </div>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default memo(ProgramCard);
